import { useState, useEffect, useContext, useCallback } from "react";

import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Tooltip,
} from "@mui/material";
import {
  postBulletinRemoveGroup,
  postBulletinShare,
  postBulletinShareRevoke,
  updateBulletinMembershipPermission,
  getBulletinAccessManagement,
  getBulletinMyAccess,
  searchBulletinGroups,
  GroupAccessInfo,
} from "@rubi/api-generated";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { AlertViewContext } from "@/components/alerts/context";
import ShareMultipleDialog from "@/components/alerts/ShareMultipleDialog";
import { AuthStateContext } from "@/contexts/AuthContext";
import { BulletinPatchPayload } from "@/types";

import { ExpireDateDialog } from "./ExpireDateDialog";
import { SearchSection } from "./SearchSection";
import { TableSection } from "./TableSection";
import {
  AccessData,
  AccessDataState,
  AccessSection,
  BulletinShareByBulletinItem,
  ExpireDateDialogState,
  PermissionLevel,
} from "./types";
import { getPermissionLevel } from "./utils";

interface AccessProps {
  bulletinId?: string | null;
}

export const Access = ({ bulletinId: bulletinIdProp }: AccessProps = {}) => {
  const [searchParams] = useSearchParams();
  const bulletinId = bulletinIdProp ?? searchParams.get("bulletinId");
  const { currentUser } = useContext(AuthStateContext);
  const { setOnlyMeHasAccess, setUpdatedItems } = useContext(AlertViewContext);
  const { t } = useTranslation("bulletin/share");

  const [accessData, setAccessData] = useState<AccessDataState>({
    me: {
      canView: false,
      canModify: false,
      isOwner: false,
    },
    my_groups: [],
    other_groups_with_access: [],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search related state
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<AccessData[]>([]);

  // Bulletin shares state
  const [bulletinShares, setBulletinShares] = useState<
    BulletinShareByBulletinItem[]
  >([]);
  const [sharesFromCurrentUser, setSharesFromCurrentUser] = useState<
    Set<number>
  >(new Set());

  // Expire date dialog state
  const [expireDateDialog, setExpireDateDialog] =
    useState<ExpireDateDialogState>({
      open: false,
      section: null,
      index: null,
      level: null,
      groupName: "",
      expireDate: null,
    });

  // Share dialog state
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  // Fetch access data using getBulletinAccessManagement.
  // showSpinner=false performs a silent background refresh so the table stays
  // mounted (scroll position preserved, no visible reload) after share actions.
  const fetchAccessData = useCallback(
    async (showSpinner = true) => {
      if (!bulletinId || isNaN(Number(bulletinId))) {
        setError(t("access_valid_bulletin_id_required"));
        setLoading(false);
        return;
      }

      try {
        if (showSpinner) {
          setLoading(true);
        }
        setError(null);

        const bulletinIdNumber = Number(bulletinId);

        // Fetch user's personal access and group access management data in parallel
        const [myAccessResponse, accessManagementResponse] = await Promise.all([
          getBulletinMyAccess({
            path: { bulletin_id: bulletinIdNumber },
          }),
          getBulletinAccessManagement({
            path: { bulletin_id: bulletinIdNumber },
          }),
        ]);

        const me = myAccessResponse.data!;
        const { groups } = accessManagementResponse.data!;

        // Transform user's personal access
        const userAccess = {
          canView: me.can_view || false,
          canModify: me.can_modify || false,
          isOwner: me.is_owner || false,
        };

        // Separate groups into user's groups and others with access
        const myGroupsData: AccessData[] = [];
        const otherGroupsWithAccess: AccessData[] = [];

        // Collect bulletin shares
        const shares: BulletinShareByBulletinItem[] = [];
        const currentUserShares = new Set<number>();

        groups.forEach((group) => {
          const hasAccess =
            group.bulletin_access !== null &&
            group.bulletin_access !== undefined;
          const hasPendingShare =
            group.pending_share !== null && group.pending_share !== undefined;

          // Compute permissions from bulletin_access
          // canView: If bulletin_access exists (group has access to bulletin)
          // canModify: If bulletin_access.is_content_moderator is true
          const canView = hasAccess;
          const canModify =
            hasAccess && (group.bulletin_access?.is_content_moderator || false);
          const isOwner =
            hasAccess && (group.bulletin_access?.is_owner || false);

          // Transform to AccessData format
          const accessDataItem: AccessData = {
            id: group.id,
            name: group.name,
            agency: group.agency || group.name,
            canView,
            canModify,
            isOwner,
            hasPendingShare,
            reshareScope: "Unrestricted",
            bulletinShareId: group.bulletin_access?.id,
            time_expires: group.bulletin_access?.time_expires
              ? new Date(group.bulletin_access.time_expires)
              : null,
            isPublic: group.is_public || false,
            isPoliceService: group.is_police_service || false,
            requires_admittance_moderation:
              group.requires_admittance_moderation || false,
            canShareToGroup: group.can_share_to_group || false,
            canShareReason: group.can_share_reason || "",
            canRemoveFromBulletin: group.can_remove_from_bulletin || false,
            canRemoveReason: group.can_remove_reason || "",
          };

          // Categorize groups
          if (group.user_is_member) {
            // All groups the user belongs to (whether they have access or not)
            myGroupsData.push(accessDataItem);
          } else if (!group.user_is_member && hasAccess) {
            // Groups user doesn't belong to but have access to the bulletin
            otherGroupsWithAccess.push(accessDataItem);
          }

          // Collect pending shares for bulletinShares state
          if (hasPendingShare && group.pending_share) {
            const shareItem: BulletinShareByBulletinItem = {
              bulletin_share: group.pending_share,
              destination_group_membership: null,
              user_role: "source_member",
            };
            shares.push(shareItem);

            // Check if this share is from current user
            if (
              currentUser &&
              group.pending_share.source_user_id === currentUser.id
            ) {
              currentUserShares.add(group.pending_share.id);
            }
          }
        });

        // Sort user's groups: public groups first, then alphabetically
        myGroupsData.sort((a, b) => {
          if (a.isPublic && !b.isPublic) return -1;
          if (!a.isPublic && b.isPublic) return 1;
          return a.name.localeCompare(b.name);
        });

        // Set states
        setBulletinShares(shares);
        setSharesFromCurrentUser(currentUserShares);

        setAccessData({
          me: userAccess,
          my_groups: myGroupsData,
          other_groups_with_access: otherGroupsWithAccess,
        });

        const onlyMe =
          myGroupsData.filter((g) => g.canView || g.canModify).length === 0 &&
          otherGroupsWithAccess.length === 0 &&
          shares.length === 0;
        setOnlyMeHasAccess(onlyMe);
      } catch (err) {
        console.error("Error fetching access data:", err);
        setError(t("access_failed_load"));
      } finally {
        if (showSpinner) {
          setLoading(false);
        }
      }
    },
    [bulletinId, currentUser],
  );

  // Call fetchAccessData when component mounts or dependencies change
  useEffect(() => {
    fetchAccessData();
  }, [fetchAccessData]);

  // Reset guard when Access unmounts (user leaves access tab)
  useEffect(() => {
    return () => setOnlyMeHasAccess(false);
  }, [setOnlyMeHasAccess]);

  // Run the server-side group search for the current search term. Extracted so
  // it can also be re-run inline after a share action to keep search-result
  // rows in sync without reloading the whole view.
  const runSearch = useCallback(async () => {
    const trimmedTerm = searchTerm.trim();
    if (!trimmedTerm) {
      setSearchResults([]);
      return;
    }

    const bulletinIdNumber = Number(bulletinId);
    if (!bulletinId || isNaN(bulletinIdNumber)) {
      return;
    }

    try {
      const response = await searchBulletinGroups({
        path: { bulletin_id: bulletinIdNumber },
        query: { q: trimmedTerm },
      });

      if (response.data) {
        const results: AccessData[] = response.data.map(
          (group: GroupAccessInfo) => {
            const hasAccess =
              group.bulletin_access !== null &&
              group.bulletin_access !== undefined;
            const hasPendingShare =
              group.pending_share !== null && group.pending_share !== undefined;
            const canView = hasAccess;
            const canModify =
              hasAccess &&
              (group.bulletin_access?.is_content_moderator || false);
            const isOwner =
              hasAccess && (group.bulletin_access?.is_owner || false);

            return {
              id: group.id,
              name: group.name,
              agency: group.agency || group.name,
              canView,
              canModify,
              isOwner,
              hasPendingShare,
              reshareScope: "Unrestricted",
              bulletinShareId: group.bulletin_access?.id,
              time_expires: group.bulletin_access?.time_expires
                ? new Date(group.bulletin_access.time_expires)
                : null,
              isPublic: group.is_public || false,
              isPoliceService: group.is_police_service || false,
              requires_admittance_moderation:
                group.requires_admittance_moderation || false,
              canShareToGroup: group.can_share_to_group || false,
              canShareReason: group.can_share_reason || "",
              canRemoveFromBulletin: group.can_remove_from_bulletin || false,
              canRemoveReason: group.can_remove_reason || "",
            };
          },
        );
        setSearchResults(results);
      }
    } catch (err) {
      console.error("Error searching groups:", err);
    }
  }, [searchTerm, bulletinId]);

  // Search functionality - debounced server-side search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      runSearch();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, bulletinId, runSearch]);

  // Signal the left-side bulletin list to refetch with its current filters so
  // it stays in sync after a group's access changes (e.g. unshare). The backend
  // re-applies the active group filter, so a bulletin is dropped only when it no
  // longer matches any selected group. An empty marker is enough — AlertList
  // consumes updatedItems purely as a refresh flag.
  const refreshBulletinList = () =>
    setUpdatedItems((prev) => [...prev, {} as BulletinPatchPayload]);

  const handleExpireDateSubmit = async () => {
    const { section, index, level, expireDate } = expireDateDialog;

    if (!section) {
      return;
    }

    try {
      // Set the selected date to end of day (23:59:59)
      let timeExpires: Date | null = null;
      if (expireDate) {
        timeExpires = expireDate.endOf("day").toDate();
      }

      // Close dialog
      setExpireDateDialog({
        open: false,
        section: null,
        index: null,
        level: null,
        groupName: "",
        expireDate: null,
      });

      // Check if approval is required
      let targetGroup: any = null;
      if (section === "search_results" && index !== null) {
        targetGroup = searchResults[index];
      } else if (index !== null) {
        targetGroup = accessData[section][index];
      }

      // If level is null, we're only updating expiration date
      if (level === null) {
        // Get current permission level
        const currentLevel = targetGroup
          ? getPermissionLevel(targetGroup.canView, targetGroup.canModify)
          : "none";

        // Update expiration with current permission level
        await updatePermissionWithExpiration(
          section,
          index,
          currentLevel,
          timeExpires,
        );
      } else {
        // We're setting a new permission level with expiration
        await updatePermissionWithExpiration(
          section,
          index,
          level,
          timeExpires,
        );
      }
    } catch (error) {
      console.error("Error updating permission with expiration:", error);
      const errorMessage =
        (error as any)?.detail?.msg ??
        (error as any)?.message ??
        t("access_failed_update_expiration");
      setError(errorMessage);
    }
  };

  const updatePermissionWithExpiration = async (
    section: AccessSection,
    index: number | null,
    level: PermissionLevel,
    timeExpires: Date | null = null,
  ): Promise<any> => {
    // Cannot modify "me" section, return directly
    if (section === "me") {
      return;
    }

    try {
      let targetGroup: any = null;

      // Get target group
      if (section === "search_results" && index !== null) {
        targetGroup = searchResults[index];
      } else if (index !== null) {
        targetGroup = accessData[section][index];
      }

      if (!targetGroup) return;

      // For public groups, convert 'full' access to 'read' access
      if (targetGroup.isPublic && level === "full") {
        level = "read";
      }

      const groupId = targetGroup.id;
      const bulletinIdNumber = parseInt(bulletinId!);

      if (level === "none") {
        // Revoke permissions
        const hasBulletinAccess = targetGroup.canView || targetGroup.canModify;

        if (hasBulletinAccess && targetGroup.canRemoveFromBulletin) {
          await postBulletinRemoveGroup({
            path: { bulletin_id: bulletinIdNumber },
            body: { id: groupId },
          });
          // Group's access was removed — keep the left-side list in sync.
          refreshBulletinList();
        }
      } else {
        // Check if there's an existing membership
        const hasExistingMembership =
          targetGroup.canView || targetGroup.canModify; // Group already has access via membership

        if (hasExistingMembership) {
          // Update existing membership permissions
          await updateBulletinMembershipPermission({
            path: { bulletin_id: bulletinIdNumber },
            body: {
              group_id: groupId,
              permission_level: level, // "read" or "full"
              time_expires: timeExpires ? timeExpires.toISOString() : null,
            },
          });
        } else {
          // Create new share to grant permissions
          const shareData = {
            bulletin_id: bulletinIdNumber,
            destination_group_id: groupId,
            time_expires: timeExpires ? timeExpires.toISOString() : null,
          };

          await postBulletinShare({
            body: shareData,
          });
        }
        // Group was granted access — keep the left-side list in sync.
        refreshBulletinList();
      }

      // Silently refresh from backend so the affected row updates in place,
      // keeping scroll position and avoiding a full reload of the share view.
      await fetchAccessData(false);
      if (searchTerm.trim()) {
        await runSearch();
      }
    } catch (error) {
      console.error("Error updating permission:", error);
      const errorMessage =
        (error as any)?.response?.data?.detail?.msg ??
        (error as any)?.response?.data?.detail ??
        (error as any)?.message ??
        t("access_failed_update_permission");
      setError(errorMessage);
    }
  };

  const updatePermission = async (
    section: AccessSection,
    index: number | null,
    level: PermissionLevel,
  ) => {
    // Cannot modify "me" section, return directly
    if (section === "me") {
      return;
    }

    let targetGroup: any = null;

    // Get target group
    if (section === "search_results" && index !== null) {
      targetGroup = searchResults[index];
    } else if (index !== null) {
      targetGroup = accessData[section][index];
    }

    if (!targetGroup) return;

    // For public groups, convert 'full' access to 'read' access
    if (targetGroup.isPublic && level === "full") {
      level = "read";
    }

    // Check if it's a public group and granting permission (not none)
    const isGrantingPermission = level !== "none";
    const isPublicGroup = targetGroup.isPublic;

    if (isPublicGroup && isGrantingPermission) {
      // For public group permission granting, show expire date selection dialog
      // Set default expiration time to 30 days later
      const defaultExpireDate = dayjs().add(30, "days");

      setExpireDateDialog({
        open: true,
        section,
        index,
        level,
        groupName: targetGroup.name,
        expireDate: defaultExpireDate,
      });
      return;
    }

    // For non-public groups or revoking permissions, execute update directly
    await updatePermissionWithExpiration(section, index, level, null);
  };

  const handleExpireDateChange = (
    section: AccessSection,
    index: number | null,
  ) => {
    let targetGroup: any = null;
    if (section === "search_results" && index !== null) {
      targetGroup = searchResults[index];
    } else if (section !== "me" && index !== null) {
      targetGroup = accessData[section][index];
    } else if (section === "me") {
      // Cannot modify expiration for "me" section
      return;
    }

    if (!targetGroup) return;

    // Check if the group has any access (can view or can modify)
    const hasAccess = targetGroup.canView || targetGroup.canModify;
    if (!hasAccess) {
      // Cannot update expiration for groups with no access
      return;
    }

    // Check if user has permission to remove from bulletin (required to change expiration)
    if (!targetGroup.canRemoveFromBulletin) {
      // Cannot update expiration without remove permission
      return;
    }

    const currentExpireDate = targetGroup.time_expires
      ? dayjs(targetGroup.time_expires)
      : dayjs().add(30, "days");

    setExpireDateDialog({
      open: true,
      section,
      index,
      level: null, // Not setting a permission level, just updating expiration
      groupName: targetGroup.name,
      expireDate: currentExpireDate,
    });
  };

  const handleRevoke = async (bulletinShareId: number) => {
    try {
      await postBulletinShareRevoke({
        path: { bulletin_share_id: bulletinShareId },
      });

      // Silently refresh so the row updates in place without reloading the view.
      await fetchAccessData(false);
      if (searchTerm.trim()) {
        await runSearch();
      }
      // A share was revoked — keep the left-side list in sync.
      refreshBulletinList();
    } catch (error) {
      console.error("Error revoking share:", error);
      setError(t("access_failed_revoke"));
    }
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          boxSizing: "border-box",
          pl: { xs: 1.5, sm: "2rem" },
          pr: { xs: 1.5, sm: "3rem" },
          pt: { xs: 2, sm: "2rem" },
        }}
      >
        <div>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h6" gutterBottom>
                {t("access_management")}
              </Typography>
            </Box>
            {bulletinId && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => setIsShareModalOpen(true)}
                sx={{ ml: 2 }}
              >
                {t("access_notify_by_email")}
              </Button>
            )}
          </Box>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && (
            <TableContainer sx={{ overflow: { xs: "auto", sm: "visible" } }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("access_group")}</TableCell>
                    <TableCell sx={{ minWidth: 50, width: "40%" }}>
                      {t("access_permission_level")}
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", sm: "table-cell" } }}
                    >
                      <Tooltip title={t("access_resharing_scope_tooltip")}>
                        <Typography variant="body2">
                          {t("access_resharing_scope")}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", sm: "table-cell" } }}
                    >
                      {t("access_expires")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableSection
                    title=""
                    section="me"
                    data={[
                      {
                        name: t("access_me"),
                        canView: accessData.me.canView,
                        canModify: accessData.me.canModify,
                        isOwner: accessData.me.isOwner,
                        reshareScope: accessData.me.isOwner
                          ? t("access_unrestricted")
                          : "-",
                      },
                    ]}
                    bulletinShares={bulletinShares}
                    sharesFromCurrentUser={sharesFromCurrentUser}
                    onExpireDateChange={handleExpireDateChange}
                    onPermissionChange={updatePermission}
                    onRevoke={handleRevoke}
                  />

                  {accessData.my_groups.length > 0 && (
                    <TableSection
                      title={t("access_my_groups")}
                      section="my_groups"
                      data={accessData.my_groups}
                      bulletinShares={bulletinShares}
                      sharesFromCurrentUser={sharesFromCurrentUser}
                      onExpireDateChange={handleExpireDateChange}
                      onPermissionChange={updatePermission}
                      onRevoke={handleRevoke}
                    />
                  )}

                  {accessData.other_groups_with_access.length > 0 && (
                    <TableSection
                      title={t("access_other_groups")}
                      section="other_groups_with_access"
                      data={accessData.other_groups_with_access}
                      bulletinShares={bulletinShares}
                      sharesFromCurrentUser={sharesFromCurrentUser}
                      onExpireDateChange={handleExpireDateChange}
                      onPermissionChange={updatePermission}
                      onRevoke={handleRevoke}
                    />
                  )}

                  <SearchSection
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                  />

                  {searchTerm && (
                    <TableSection
                      title=""
                      section="search_results"
                      data={searchResults}
                      showDivider={false}
                      searchTerm={searchTerm}
                      bulletinShares={bulletinShares}
                      sharesFromCurrentUser={sharesFromCurrentUser}
                      onExpireDateChange={handleExpireDateChange}
                      onPermissionChange={updatePermission}
                      onRevoke={handleRevoke}
                    />
                  )}

                  {accessData.my_groups.length === 0 &&
                    accessData.other_groups_with_access.length === 0 &&
                    !searchTerm && (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          sx={{
                            textAlign: "center",
                            py: 3,
                            color: "text.secondary",
                          }}
                        >
                          {t("access_no_group_data")}
                        </TableCell>
                      </TableRow>
                    )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
      </Box>

      <ExpireDateDialog
        expireDateDialog={expireDateDialog}
        onClose={() =>
          setExpireDateDialog((prev) => ({ ...prev, open: false }))
        }
        onUpdate={(updates) =>
          setExpireDateDialog((prev) => ({ ...prev, ...updates }))
        }
        onSubmit={handleExpireDateSubmit}
      />

      {bulletinId && (
        <ShareMultipleDialog
          isShareModalOpenArg={isShareModalOpen}
          setIsShareModalOpenArg={setIsShareModalOpen}
          bulletinIDs={[Number(bulletinId)]}
        />
      )}
    </>
  );
};
